/**
 * Source scanning shared by the importers: code fences, JSX elements, and
 * colon-fenced containers.
 *
 * The importers have the same three problems, so the answer lives once, here.
 *
 * Problem 1: a documentation site documents its own syntax. A page that shows
 * `:::note` or `<Tabs>` inside a code fence is showing an EXAMPLE, and rewriting
 * it corrupts the example. So every transform runs outside code spans, and the
 * scanner treats a fenced block as opaque.
 *
 * Problem 2: JSX is not Markdown and not HTML. `<Tab title="A" default>` has
 * bare attributes, `<Step icon={<Icon/>}>` has expression attributes with
 * angle brackets inside braces, and elements nest with the same tag name
 * (`<Tabs>` inside `<Tab>`). A regular expression cannot match that; the scanner
 * below counts depth instead, which is why it is a scanner and not a regex.
 *
 * It is a SCANNER, not a parser. It knows nothing about JavaScript semantics: an
 * expression attribute is kept as its raw text so that nothing is lost, and the
 * caller reports it rather than pretending to understand it.
 */
/**
 * Fenced code blocks, including the fence lines.
 *
 * Indented code blocks are deliberately NOT included. Inside JSX children,
 * indentation is structural rather than semantic, and treating four spaces of
 * JSX indentation as code would make the scanner blind to the tags in it.
 */
export function codeSpans(text) {
    const spans = [];
    let offset = 0;
    let open;
    /**
     * An indented code block, which is code just as much as a fenced one.
     *
     * This used to know only about fences, so every importer built on
     * `replaceOutsideCode` — link rewriting, path rewriting, attribute lists —
     * silently edited the CONTENTS of four-space code blocks. A documentation
     * page showing `{: .class }` or `@site/static/x.png` as an example had the
     * example rewritten, which is a documentation tool damaging the
     * documentation of the thing it is importing.
     *
     * Deliberately conservative about what counts. CommonMark says an indented
     * chunk is code only outside a list, and a four-space indent inside a list
     * is a continuation paragraph — extremely common in documentation. So a run
     * only counts when the line before the blank line that opens it is NOT a
     * list item or itself indented. Getting this wrong in the cautious
     * direction means an importer leaves something alone; getting it wrong the
     * other way means it edits somebody's code.
     */
    let indented;
    let blankBefore = true;
    let listContext = false;
    const lines = text.split('\n');
    for (const line of lines) {
        const fence = line.match(/^\s{0,3}(`{3,}|~{3,})/);
        if (open === undefined) {
            if (fence) {
                if (indented) {
                    spans.push([indented.start, offset]);
                    indented = undefined;
                }
                open = { marker: fence[1][0].repeat(3), start: offset };
                offset += line.length + 1;
                blankBefore = false;
                continue;
            }
        }
        else {
            if (fence && fence[1].startsWith(open.marker)) {
                spans.push([open.start, offset + line.length]);
                open = undefined;
            }
            offset += line.length + 1;
            continue;
        }
        const blank = line.trim() === '';
        const deep = /^(?: {4}|\t)/.test(line);
        if (indented !== undefined) {
            // A blank line does not end an indented block — a code sample may have
            // blank lines in it — but an unindented, non-blank line does.
            if (!deep && !blank) {
                spans.push([indented.start, offset]);
                indented = undefined;
            }
        }
        else if (deep && !blank && blankBefore && !listContext) {
            indented = { start: offset };
        }
        if (!blank) {
            // What the NEXT indented run would be a continuation of.
            if (!deep)
                listContext = /^\s{0,3}(?:[-*+]|\d+[.)])\s/.test(line);
            blankBefore = false;
        }
        else {
            blankBefore = true;
        }
        offset += line.length + 1;
    }
    // An unterminated fence runs to the end of the file, which is how Markdown
    // itself reads it.
    if (open !== undefined)
        spans.push([open.start, text.length]);
    if (indented !== undefined)
        spans.push([indented.start, text.length]);
    return spans;
}
export function inSpans(spans, index) {
    return spans.some(([start, end]) => index >= start && index < end);
}
/**
 * Replace every match of `pattern` that starts outside a code fence.
 *
 * `pattern` must be global. The replacer returns the replacement, or undefined
 * to leave the match alone.
 */
export function replaceOutsideCode(text, pattern, replacer) {
    const spans = codeSpans(text);
    let out = '';
    let last = 0;
    for (const match of text.matchAll(pattern)) {
        const at = match.index;
        if (at === undefined || inSpans(spans, at))
            continue;
        const replacement = replacer(match);
        if (replacement === undefined)
            continue;
        out += text.slice(last, at) + replacement;
        last = at + match[0].length;
    }
    return out + text.slice(last);
}
/**
 * Split into lines, marking the fenced ones.
 *
 * The MkDocs and Docsify transforms need lookahead over following lines, which
 * a regular expression over the whole document cannot do safely.
 */
export function sourceLines(text) {
    const out = [];
    let marker;
    for (const line of text.split('\n')) {
        const fence = line.match(/^\s{0,3}(`{3,}|~{3,})/);
        if (marker === undefined) {
            if (fence) {
                marker = fence[1][0].repeat(3);
                out.push({ text: line, inCode: true, isFence: true });
            }
            else {
                out.push({ text: line, inCode: false, isFence: false });
            }
        }
        else if (fence && fence[1].startsWith(marker)) {
            marker = undefined;
            out.push({ text: line, inCode: true, isFence: true });
        }
        else {
            out.push({ text: line, inCode: true, isFence: false });
        }
    }
    return out;
}
const NAME = /[A-Za-z][A-Za-z0-9._-]*/;
/**
 * Find the outermost JSX elements whose name `accept` returns true for.
 *
 * Outermost only, and non-overlapping. Nesting is handled by the caller calling
 * again on `children`, so each importer controls the order in which it converts
 * an outer and an inner element.
 */
export function findJsxElements(text, accept) {
    const spans = codeSpans(text);
    const found = [];
    let index = 0;
    while (index < text.length) {
        const at = text.indexOf('<', index);
        if (at === -1)
            break;
        if (inSpans(spans, at)) {
            index = at + 1;
            continue;
        }
        const name = NAME.exec(text.slice(at + 1, at + 60));
        if (!name || name.index !== 0 || !accept(name[0])) {
            index = at + 1;
            continue;
        }
        const open = readOpenTag(text, at);
        if (!open) {
            index = at + 1;
            continue;
        }
        const tagName = name[0];
        if (open.selfClosing) {
            found.push({
                name: tagName,
                ...parseAttributes(open.attrs),
                children: '',
                raw: text.slice(at, open.end),
                start: at,
                end: open.end,
                selfClosing: true,
            });
            index = open.end;
            continue;
        }
        const close = findClosingTag(text, tagName, open.end, spans);
        if (close === undefined) {
            // An unclosed tag is malformed JSX. Leave it exactly as written: guessing
            // where it ends is how an importer eats the rest of a page.
            index = open.end;
            continue;
        }
        found.push({
            name: tagName,
            ...parseAttributes(open.attrs),
            children: text.slice(open.end, close.start),
            raw: text.slice(at, close.end),
            start: at,
            end: close.end,
            selfClosing: false,
        });
        index = close.end;
    }
    return found;
}
/** Replace the outermost accepted elements. `undefined` leaves one alone. */
export function replaceJsxElements(text, accept, replacer) {
    const elements = findJsxElements(text, accept);
    let out = '';
    let last = 0;
    for (const element of elements) {
        const replacement = replacer(element);
        if (replacement === undefined)
            continue;
        out += text.slice(last, element.start) + replacement;
        last = element.end;
    }
    return out + text.slice(last);
}
/** Read `<Name ...>` or `<Name ... />`, respecting quotes and `{}` nesting. */
function readOpenTag(text, at) {
    let i = at + 1;
    while (i < text.length && /[A-Za-z0-9._-]/.test(text[i]))
        i++;
    const attrsFrom = i;
    let quote;
    let braces = 0;
    for (; i < text.length; i++) {
        const char = text[i];
        if (quote) {
            if (char === quote)
                quote = undefined;
            continue;
        }
        if (char === '"' || char === "'") {
            quote = char;
            continue;
        }
        if (char === '{')
            braces++;
        else if (char === '}')
            braces--;
        else if (char === '>' && braces === 0) {
            const attrs = text.slice(attrsFrom, i);
            const selfClosing = attrs.trimEnd().endsWith('/');
            return {
                attrs: selfClosing ? attrs.trimEnd().slice(0, -1) : attrs,
                end: i + 1,
                selfClosing,
            };
        }
    }
    return undefined;
}
/** Find `</Name>`, counting nested `<Name ...>` opens. */
function findClosingTag(text, name, from, spans) {
    let depth = 0;
    let i = from;
    while (i < text.length) {
        const at = text.indexOf('<', i);
        if (at === -1)
            return undefined;
        if (inSpans(spans, at)) {
            i = at + 1;
            continue;
        }
        const rest = text.slice(at);
        // The boundary check matters: `<Tab>` must not count as an open tag for
        // `<Tabs>`, and `</Tabs>` must not close `<Tab>`.
        const boundary = (offset) => !/^[A-Za-z0-9._-]/.test(rest.slice(offset));
        const closing = rest.startsWith(`</${name}`) && boundary(2 + name.length) && rest.includes('>');
        const opening = rest.startsWith(`<${name}`) && boundary(1 + name.length);
        if (closing) {
            if (depth === 0) {
                const end = text.indexOf('>', at);
                return { start: at, end: end === -1 ? text.length : end + 1 };
            }
            depth--;
            i = at + 1;
            continue;
        }
        if (opening) {
            const open = readOpenTag(text, at);
            if (open && !open.selfClosing)
                depth++;
            i = open ? open.end : at + 1;
            continue;
        }
        i = at + 1;
    }
    return undefined;
}
const OPEN = /^(:{3,})[ \t]*([A-Za-z][A-Za-z0-9_-]*)[ \t]*(?:\[([^\]]*)\])?[ \t]*(.*)$/;
const CLOSE = /^(:{3,})[ \t]*$/;
/**
 * Convert `:::name` containers, innermost first.
 *
 * Both Docusaurus admonitions and VitePress containers use this syntax, and
 * both nest, so the fence widths have to be computed from the inside out.
 * Returning `undefined` from `convert` leaves a block exactly as it was
 * written, which is what an unrecognised container must get.
 */
export function convertColonContainers(text, convert) {
    const root = [];
    const stack = [];
    const current = () => (stack.length > 0 ? stack[stack.length - 1].items : root);
    for (const line of sourceLines(text)) {
        if (line.inCode) {
            current().push({ line: line.text });
            continue;
        }
        const open = OPEN.exec(line.text);
        if (open) {
            stack.push({
                colons: open[1].length,
                name: open[2],
                ...(open[3] === undefined ? {} : { title: open[3] }),
                trailing: open[4].trim(),
                items: [],
                openLine: line.text,
            });
            continue;
        }
        const close = CLOSE.exec(line.text);
        if (close && stack.length > 0) {
            const node = stack.pop();
            current().push({ block: node });
            continue;
        }
        current().push({ line: line.text });
    }
    // An unclosed container is malformed. Put its lines back rather than guessing
    // where the author meant it to end.
    while (stack.length > 0) {
        const node = stack.pop();
        current().push({ line: node.openLine });
        for (const item of node.items)
            current().push(item);
    }
    return render(root, convert);
}
function render(items, convert) {
    const out = [];
    for (const item of items) {
        if ('line' in item) {
            out.push(item.line);
            continue;
        }
        const node = item.block;
        const body = render(node.items, convert);
        const width = fenceWidth(body);
        const title = node.title ?? (node.trailing.length > 0 ? node.trailing : undefined);
        const converted = convert({
            name: node.name,
            ...(title === undefined ? {} : { title }),
            body,
            width,
        });
        if (converted === undefined) {
            out.push(node.openLine, body, ':'.repeat(node.colons));
        }
        else {
            out.push(converted);
        }
    }
    return out.join('\n');
}
/** The widest `:::` fence in a block, so the block around it can be wider. */
function widestFence(text) {
    let widest = 0;
    for (const match of text.matchAll(/^(:{3,})/gm))
        widest = Math.max(widest, match[1].length);
    return widest;
}
/**
 * The fence width a directive needs in order to CONTAIN this body.
 *
 * Always call this rather than guessing a generous width. A directive whose
 * fence is no wider than one inside it does not nest — the inner fence closes
 * the outer block, and everything after it lands outside. Measuring the body is
 * the only way to be right when the nesting depth is not known in advance.
 */
export function fenceWidth(body) {
    return Math.max(3, widestFence(body) + 1);
}
export function isOpaque(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value) && 'opaque' in value;
}
/**
 * Read the first object or array literal assigned in a JS/TS config file.
 *
 * Docusaurus keeps its sidebar in `sidebars.js` and VitePress keeps its sidebar
 * in `.vitepress/config.ts`. Both are CODE: the only fully correct reader is a
 * JavaScript engine, and running a downloaded export's JavaScript to import it
 * is not a trade this tool will make.
 *
 * So this is a SCANNER over the literal syntax. It handles strings, numbers,
 * booleans, arrays, objects, comments, and `defineConfig({...})`-style wrappers.
 * Anything else becomes opaque and is reported. A caller must always have a
 * fallback — the directory tree — for the sites this cannot read.
 */
export function findAssignedLiteral(source) {
    // `export default defineConfig({...})` has no `=` at all, which is how every
    // VitePress config is written. Requiring one sent the scanner off to parse
    // the `{ defineConfig }` of the import statement instead.
    const assignment = /(?:module\.exports\s*=|export\s+default|(?:const|let|var)\s+[A-Za-z_$][\w$]*(?:\s*:[^=\n]+)?\s*=)/.exec(source);
    let at = assignment ? assignment.index + assignment[0].length : 0;
    // Step over `defineConfig(`, `satisfies X`, and any wrapper call.
    while (at < source.length) {
        at = skipTrivia(source, at);
        const char = source[at];
        if (char === '{' || char === '[')
            break;
        if (char === undefined)
            return undefined;
        if (/[A-Za-z_$.(]/.test(char)) {
            at++;
            continue;
        }
        return undefined;
    }
    return parseJsValue(source, at)?.value;
}
/** Parse one literal at `at`. Exported for callers that know where to look. */
export function parseJsValue(source, at) {
    let i = skipTrivia(source, at);
    const char = source[i];
    if (char === undefined)
        return undefined;
    if (char === '"' || char === "'" || char === '`') {
        const end = closingQuote(source, i);
        const raw = source.slice(i + 1, end);
        // A template literal with an expression is code, not a string.
        if (char === '`' && raw.includes('${'))
            return { value: { opaque: raw }, end: end + 1 };
        return { value: raw, end: end + 1 };
    }
    if (char === '[') {
        const items = [];
        i++;
        for (;;) {
            i = skipTrivia(source, i);
            if (source[i] === ']')
                return { value: items, end: i + 1 };
            if (source[i] === undefined)
                return { value: items, end: source.length };
            if (source[i] === ',') {
                i++;
                continue;
            }
            const parsed = parseJsValue(source, i);
            if (!parsed)
                return { value: items, end: i };
            items.push(parsed.value);
            i = parsed.end;
        }
    }
    if (char === '{') {
        const out = {};
        i++;
        for (;;) {
            i = skipTrivia(source, i);
            if (source[i] === '}')
                return { value: out, end: i + 1 };
            if (source[i] === undefined)
                return { value: out, end: source.length };
            if (source[i] === ',') {
                i++;
                continue;
            }
            const quoted = source[i] === '"' || source[i] === "'";
            let key;
            if (quoted) {
                const end = closingQuote(source, i);
                key = source.slice(i + 1, end);
                i = end + 1;
            }
            else {
                const name = /^[A-Za-z_$][\w$]*/.exec(source.slice(i));
                if (!name) {
                    // A spread or a computed key. Skip to the next comma at this depth.
                    i = skipToDelimiter(source, i);
                    continue;
                }
                key = name[0];
                i += key.length;
            }
            i = skipTrivia(source, i);
            if (source[i] !== ':') {
                // Shorthand property: the value is an identifier we cannot resolve.
                out[key] = { opaque: key };
                continue;
            }
            i++;
            const parsed = parseJsValue(source, i);
            if (!parsed)
                return { value: out, end: i };
            out[key] = parsed.value;
            i = parsed.end;
        }
    }
    const word = /^(true|false|null|-?\d+(?:\.\d+)?)/.exec(source.slice(i));
    if (word) {
        const raw = word[0];
        const value = raw === 'true' ? true : raw === 'false' ? false : raw === 'null' ? null : Number(raw);
        return { value, end: i + raw.length };
    }
    const end = skipToDelimiter(source, i);
    return { value: { opaque: source.slice(i, end).trim() }, end };
}
function skipTrivia(source, at) {
    let i = at;
    for (;;) {
        while (i < source.length && /\s/.test(source[i]))
            i++;
        if (source.startsWith('//', i)) {
            const end = source.indexOf('\n', i);
            i = end === -1 ? source.length : end + 1;
            continue;
        }
        if (source.startsWith('/*', i)) {
            const end = source.indexOf('*/', i);
            i = end === -1 ? source.length : end + 2;
            continue;
        }
        return i;
    }
}
function closingQuote(source, at) {
    const quote = source[at];
    for (let i = at + 1; i < source.length; i++) {
        if (source[i] === '\\') {
            i++;
            continue;
        }
        if (source[i] === quote)
            return i;
    }
    return source.length;
}
/** Skip an expression we do not parse, stopping at the enclosing delimiter. */
function skipToDelimiter(source, at) {
    let depth = 0;
    for (let i = at; i < source.length; i++) {
        const char = source[i];
        if (char === '"' || char === "'" || char === '`') {
            i = closingQuote(source, i);
            continue;
        }
        if (char === '(' || char === '[' || char === '{')
            depth++;
        else if (char === ')' || char === ']' || char === '}') {
            if (depth === 0)
                return i;
            depth--;
        }
        else if (char === ',' && depth === 0)
            return i;
    }
    return source.length;
}
/** Split an open tag's attribute text into literals and raw expressions. */
export function parseAttributes(source) {
    const attributes = {};
    const expressions = {};
    let i = 0;
    while (i < source.length) {
        while (i < source.length && /\s/.test(source[i]))
            i++;
        const name = NAME.exec(source.slice(i));
        if (!name || name.index !== 0) {
            i++;
            continue;
        }
        const key = name[0];
        i += key.length;
        while (i < source.length && /\s/.test(source[i]))
            i++;
        if (source[i] !== '=') {
            // A bare attribute, as in `<Tab default>`. JSX reads it as `true`.
            attributes[key] = 'true';
            continue;
        }
        i++;
        while (i < source.length && /\s/.test(source[i]))
            i++;
        const char = source[i];
        if (char === '"' || char === "'") {
            const end = source.indexOf(char, i + 1);
            attributes[key] = source.slice(i + 1, end === -1 ? source.length : end);
            i = end === -1 ? source.length : end + 1;
            continue;
        }
        if (char === '{') {
            let depth = 0;
            let j = i;
            for (; j < source.length; j++) {
                if (source[j] === '{')
                    depth++;
                else if (source[j] === '}') {
                    depth--;
                    if (depth === 0)
                        break;
                }
            }
            expressions[key] = source.slice(i + 1, j).trim();
            i = j + 1;
            continue;
        }
        // An unquoted value is not valid JSX. Take the word and let the caller
        // report it rather than dropping the attribute.
        const word = source.slice(i).match(/^\S+/);
        attributes[key] = word ? word[0] : '';
        i += word ? word[0].length : 1;
    }
    return { attributes, expressions };
}
/**
 * An HTML table into a Markdown table.
 *
 * MEASURED, and this is the whole reason: an imported HTML table arrives in the
 * editor as an `unknownBlock`, which a writer can look at and cannot touch. The
 * same table written as Markdown arrives as a `table` node, with editable cells.
 * A real GitBook export in the fixtures has an eleven-row table of scan formats,
 * and none of it was editable.
 *
 * Regex rather than a DOM parser, deliberately. This package is bundled into the
 * browser for the editor, so a Node-only parser would break that build — proved
 * once already today by `node:path` — and the shapes real exports produce are
 * narrow: one `<table>`, optional `<thead>`, `<tr>` of `<th>` or `<td>`.
 *
 * It REFUSES rather than mangles. A Markdown table cannot express a merged cell
 * or a block inside a cell, so a table with either is left as HTML and reported.
 * Half a table is worse than a table somebody has to convert by hand: the first
 * is a silent lie about their content.
 */
export function convertHtmlTables(text, note) {
    let converted = 0;
    let refusedSpan = 0;
    let refusedBlock = 0;
    let widths = 0;
    const out = text.replace(/<table[\s>][\s\S]*?<\/table>/gi, (table) => {
        // A merged cell has no Markdown spelling at all.
        if (/\b(?:colspan|rowspan)\s*=/i.test(table)) {
            refusedSpan += 1;
            return table;
        }
        // A cell holding a paragraph, a list or another table. Markdown cells are
        // inline only, so flattening one would change what the page says.
        if (/<t[dh][^>]*>[\s\S]*?<(?:p|div|ul|ol|table|pre|blockquote)\b/i.test(table)) {
            refusedBlock += 1;
            return table;
        }
        const rows = [...table.matchAll(/<tr[^>]*>([\s\S]*?)<\/tr>/gi)].map((row) => [...row[1].matchAll(/<(t[dh])([^>]*)>([\s\S]*?)<\/\1>/gi)].map((cell) => ({
            head: cell[1].toLowerCase() === 'th',
            attributes: cell[2] ?? '',
            text: inlineHtmlToMarkdown(cell[3] ?? ''),
        })));
        if (rows.length === 0 || rows[0].length === 0) {
            refusedBlock += 1;
            return table;
        }
        if (/\bwidth\s*=/i.test(table))
            widths += 1;
        /**
         * The header row.
         *
         * A Markdown table must have one. `<thead>` if there is one, otherwise the
         * first row — which is what a browser shows as the top row anyway, so
         * promoting it changes how it looks and not what it says.
         */
        const [header, ...rest] = rows;
        const columns = Math.max(...rows.map((row) => row.length));
        /** `align` on a cell maps onto the delimiter row, which is where Markdown keeps it. */
        const alignment = (index) => {
            const cell = header[index];
            const align = /\balign\s*=\s*["']?(left|center|right)/i.exec(cell?.attributes ?? '')?.[1];
            if (align === 'center')
                return ':-:';
            if (align === 'right')
                return '--:';
            if (align === 'left')
                return ':--';
            return '---';
        };
        const line = (cells) => `| ${Array.from({ length: columns }, (_, i) => cells[i]?.text ?? '').join(' | ')} |`;
        converted += 1;
        return [
            line(header),
            `| ${Array.from({ length: columns }, (_, i) => alignment(i)).join(' | ')} |`,
            ...rest.map(line),
        ].join('\n');
    });
    if (converted > 0) {
        note('derived', 'html-table-converted', 'An HTML table became a Markdown table, so its cells are editable. An HTML one arrives in the editor as a block nobody can type into.');
    }
    if (widths > 0) {
        note('review', 'html-table-width', 'A table cell carried a `width`. Markdown has no column width, so it was dropped — the table renders, the columns size themselves.');
    }
    if (refusedSpan > 0) {
        note('unconverted', 'html-table-merged-cell', 'A table used `colspan` or `rowspan`, which Markdown cannot express, so it was left as HTML. It renders as before and cannot be edited as a table.');
    }
    if (refusedBlock > 0) {
        note('unconverted', 'html-table-block-cell', 'A table had a paragraph, a list or another table inside a cell, which a Markdown cell cannot hold, so it was left as HTML. Flattening it would have changed what the page says.');
    }
    return out;
}
/**
 * The inline HTML a table cell actually contains, as Markdown.
 *
 * Narrow on purpose: a link, emphasis, code and a line break are what exports
 * put in cells. Anything else is left as written — it is valid inside a Markdown
 * cell and a reader still sees it, whereas guessing at it would not be.
 */
function inlineHtmlToMarkdown(html) {
    return (html
        .replace(/<a\b[^>]*href\s*=\s*["']([^"']*)["'][^>]*>([\s\S]*?)<\/a>/gi, (_all, href, body) => `[${String(body).trim()}](${String(href).trim()})`)
        .replace(/<(?:strong|b)\b[^>]*>([\s\S]*?)<\/(?:strong|b)>/gi, (_all, body) => `**${body}**`)
        .replace(/<(?:em|i)\b[^>]*>([\s\S]*?)<\/(?:em|i)>/gi, (_all, body) => `_${body}_`)
        .replace(/<code\b[^>]*>([\s\S]*?)<\/code>/gi, (_all, body) => `\`${body}\``)
        // A break inside a cell has nowhere to go: a Markdown row is one line.
        .replace(/<br\s*\/?>/gi, ' ')
        .replace(/\s+/g, ' ')
        // A pipe would end the cell.
        .replace(/\|/g, '\\|')
        .trim());
}
//# sourceMappingURL=import-scan.js.map